import type { AdvancedFilterData } from "@redotech/redo-views-common/advanced-filters/advanced-filter-data";
import { ArrayStringFilterOperator } from "@redotech/redo-views-common/advanced-filters/array-string-filter";
import { AdvancedFilterType } from "@redotech/redo-views-common/advanced-filters/generic-advanced-filter-data";
import {
  SortDirection,
  type TableSort,
} from "@redotech/redo-views-common/table";
import {
  DEFAULT_PAGE_SIZE,
  type viewTypes,
} from "@redotech/redo-views-common/views";
import {
  ConsolidationSummaryFilterNames,
  type ConsolidationDueWindow,
  type ConsolidationSortKey,
} from "../consolidation-work-queue";

export enum ConsolidationSystemView {
  TODAY = "today",
  OVERDUE = "overdue",
  NEXT_7_DAYS = "next-7-days",
  UPCOMING = "upcoming",
  READY_TO_SHIP = "ready-to-ship",
  ALL_ACTIVE = "all-active",
}

const ACTIVE_STATUSES = ["open", "finalizing", "sealed"];
const PACKING_STATUSES = ["open", "finalizing"];

export const CONSOLIDATION_DEFAULT_COLUMNS = [
  { key: "participants", width: 200, hidden: false },
  { key: "closeDueAt", width: 160, hidden: false },
  { key: "status", width: 130, hidden: false },
  { key: "itemCount", width: 180, hidden: false },
  { key: "cartons", width: 170, hidden: false },
  { key: "route", width: 260, hidden: false },
  { key: "lastActivityAt", width: 150, hidden: false },
];

export const CONSOLIDATION_DEFAULT_SORT = sortBy(
  "closeDueAt",
  SortDirection.ASC,
);

function sortBy(
  key: ConsolidationSortKey,
  direction: SortDirection,
): TableSort {
  return { direction, key };
}

function anyOf(name: string, value: string[]): AdvancedFilterData {
  return {
    name,
    operator: ArrayStringFilterOperator.ANY_OF,
    type: AdvancedFilterType.ARRAY_TO_STRING,
    value,
  };
}

function dueWindows(windows: ConsolidationDueWindow[]): AdvancedFilterData {
  return anyOf(ConsolidationSummaryFilterNames.dueWindow, windows);
}

function statuses(values: string[]): AdvancedFilterData {
  return anyOf(ConsolidationSummaryFilterNames.status, values);
}

function systemView(
  name: ConsolidationSystemView,
  filters: AdvancedFilterData[],
  position: number,
  sort: TableSort = CONSOLIDATION_DEFAULT_SORT,
): viewTypes["consolidations"] {
  return {
    table: "consolidations",
    name,
    filters,
    columns: CONSOLIDATION_DEFAULT_COLUMNS,
    pageSize: DEFAULT_PAGE_SIZE,
    sort,
    position,
  };
}

export const StaticSystemViews: Record<
  ConsolidationSystemView,
  viewTypes["consolidations"]
> = {
  [ConsolidationSystemView.TODAY]: systemView(
    ConsolidationSystemView.TODAY,
    [statuses(PACKING_STATUSES), dueWindows(["overdue", "today"])],
    0,
  ),
  [ConsolidationSystemView.OVERDUE]: systemView(
    ConsolidationSystemView.OVERDUE,
    [statuses(ACTIVE_STATUSES), dueWindows(["overdue"])],
    1,
  ),
  [ConsolidationSystemView.NEXT_7_DAYS]: systemView(
    ConsolidationSystemView.NEXT_7_DAYS,
    [
      statuses(PACKING_STATUSES),
      dueWindows(["overdue", "today", "next_7_days"]),
    ],
    2,
  ),
  [ConsolidationSystemView.UPCOMING]: systemView(
    ConsolidationSystemView.UPCOMING,
    [statuses(PACKING_STATUSES), dueWindows(["later", "unscheduled"])],
    3,
  ),
  [ConsolidationSystemView.READY_TO_SHIP]: systemView(
    ConsolidationSystemView.READY_TO_SHIP,
    [statuses(["sealed"])],
    4,
  ),
  [ConsolidationSystemView.ALL_ACTIVE]: systemView(
    ConsolidationSystemView.ALL_ACTIVE,
    [statuses(ACTIVE_STATUSES)],
    5,
    sortBy("lastActivityAt", SortDirection.DESC),
  ),
};

const FRIENDLY_NAMES: Record<string, string> = {
  [ConsolidationSystemView.TODAY]: "Today",
  [ConsolidationSystemView.OVERDUE]: "Overdue",
  [ConsolidationSystemView.NEXT_7_DAYS]: "Next 7 days",
  [ConsolidationSystemView.UPCOMING]: "Upcoming",
  [ConsolidationSystemView.READY_TO_SHIP]: "Ready to ship",
  [ConsolidationSystemView.ALL_ACTIVE]: "All active",
};

export function friendlyConsolidationViewName(name: string): string {
  return FRIENDLY_NAMES[name] ?? name;
}
